/**
 * 翻译服务工厂
 * 负责创建、管理和缓存翻译服务实例
 *
 * 核心功能：
 * 1. 按需动态加载翻译服务模块
 * 2. 根据提供商类型创建合适的翻译器实例
 * 3. 缓存翻译器实例提高性能
 * 4. 提供翻译提供商可用性检查
 * 5. 降级处理确保系统可靠性
 */
import { TranslationProvider } from '@readsavor/types';
/**
 * 翻译服务工厂 - 根据提供商创建合适的翻译服务实例
 * 使用动态导入实现按需加载
 */
export class TranslatorFactory {
    /**
     * 获取指定提供商的翻译服务
     * @param provider 翻译提供商
     * @returns 翻译服务实例
     */
    static async getTranslator(provider) {
        // 如果已经有缓存的实例，直接返回
        if (this.translators.has(provider)) {
            return this.translators.get(provider);
        }
        let translator;
        try {
            // 动态导入所需的翻译器模块
            switch (provider) {
                case TranslationProvider.DEEPSEEK: {
                    // 动态导入DeepSeek翻译器
                    const { DeepSeekTranslator } = await import('./deepseek.translator.js');
                    translator = new DeepSeekTranslator();
                    break;
                }
                case TranslationProvider.DEEPSEEK_V3: {
                    // 动态导入DeepSeek V3翻译器
                    const { DeepSeekV3Translator } = await import('./deepseek-v3.translator.js');
                    translator = new DeepSeekV3Translator();
                    break;
                }
                case TranslationProvider.GEMINI_2_5_FLASH_LITE: {
                    const { Gemini25FlashLiteTranslator } = await import('./gemini-2.5-flash-lite.translator.js');
                    translator = new Gemini25FlashLiteTranslator();
                    break;
                }
                case TranslationProvider.GEMINI_2_5_FLASH: {
                    const { Gemini25FlashTranslator } = await import('./gemini-2.5-flash.translator.js');
                    translator = new Gemini25FlashTranslator();
                    break;
                }
                case TranslationProvider.GEMINI_2_5_PRO: {
                    const { Gemini25ProTranslator } = await import('./gemini-2.5-pro.translator.js');
                    translator = new Gemini25ProTranslator();
                    break;
                }
                default:
                    // If no provider matches, throw an error or return a default real translator
                    // For now, let's throw an error to make missing providers explicit.
                    throw new Error(`Translator for provider '${provider}' is not supported.`);
            }
            // 缓存实例
            this.translators.set(provider, translator);
            return translator;
        }
        catch (error) {
            console.error(`Error loading translator for ${provider}:`, error);
            // If loading fails, re-throw the error so the caller can handle it.
            // Returning a default/mock translator here hides the actual problem.
            console.error(`Error loading translator for ${provider}:`, error);
            throw error;
        }
    }
    /**
     * 获取所有可用的翻译提供商
     * @returns 可用翻译提供商信息列表
     */
    static async getAvailableProviders() {
        const providers = [];
        // 从枚举值中获取所有提供商
        const providerValues = Object.values(TranslationProvider);
        // 依次检查每个提供商的可用性
        for (const provider of providerValues) {
            try {
                const translator = await this.getTranslator(provider);
                const isAvailable = await translator.isAvailable();
                providers.push({
                    provider,
                    name: translator.getName(),
                    description: translator.getDescription?.() || '',
                    isAvailable,
                });
            }
            catch (error) {
                console.error(`Error checking provider ${provider}:`, error);
                // 如果检查过程出错，将该提供商标记为不可用
                providers.push({
                    provider,
                    name: provider, // 使用枚举值作为名称
                    isAvailable: false,
                });
            }
        }
        return providers;
    }
    /**
     * 清除翻译器缓存
     * 在需要释放内存或重新加载时使用
     */
    static clearCache() {
        this.translators.clear();
        // No default translator to re-add.
    }
}
// 保存已创建的翻译器实例的缓存
TranslatorFactory.translators = new Map();
TranslatorFactory.defaultTranslator = null; // Removed MockTranslator
//# sourceMappingURL=translator.factory.js.map